/**
 * The Bot Console (§7.3) — one component tree, two entry points.
 *
 * `scope` says which nav branch the actor came through (My Bots or My Rentals).
 * It changes nothing about permissions: every control is gated on the
 * server-delivered capability set (§7.4 rule 1), so there is exactly one console
 * and no second implementation to drift.
 *
 * §7.8 layout: >=1280 Compose | Queue side by side with Feed below; 768-1279
 * Compose over Queue; <768 the whole thing becomes tabbed.
 */

import { useState } from 'react';
import type { Bot, Rental } from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { useLayout } from '../../hooks/useMediaQuery';
import { ConsoleHeader } from './ConsoleHeader';
import { Compose } from './Compose';
import { Queue } from './Queue';
import { FeedSearch } from './FeedSearch';
import { AutoReply } from './AutoReply';
import { RenterCharacterPanel, RenterSchedulePanel } from './RenterPanels';

type Tab = 'compose' | 'queue' | 'feed' | 'autoreply';

export function Console({
  bot,
  caps,
  reload,
  scope,
  rental,
}: {
  bot: Bot;
  caps: CapabilityResolver;
  reload: () => void;
  scope: 'owner' | 'renter';
  rental: Rental | null;
}) {
  const layout = useLayout();
  const [tab, setTab] = useState<Tab>('compose');
  // Bumped when Compose queues something, so the Queue re-reads without waiting
  // for its 30s poll.
  const [queueToken, setQueueToken] = useState(0);

  const compose = <Compose bot={bot} caps={caps} onQueueChanged={() => setQueueToken((n) => n + 1)} />;
  const queue = <Queue bot={bot} refreshToken={queueToken} />;
  const feed = <FeedSearch bot={bot} caps={caps} />;
  // Owner-run bots only (no rental path yet) — see services/listening.ts.
  const autoreply = scope === 'owner' ? <AutoReply bot={bot} /> : null;
  const tabs: Tab[] = scope === 'owner' ? ['compose', 'queue', 'feed', 'autoreply'] : ['compose', 'queue', 'feed'];

  return (
    <div className={`console console-${layout}`}>
      <ConsoleHeader bot={bot} caps={caps} reload={reload} scope={scope} rental={rental} />

      {/*
        A renter granted edit_character or edit_schedule gets those controls here,
        inside the Console — never as a new top-level nav section, because a
        renter must not see a nav branch that is mostly locked to them (§7.1).
      */}
      {scope === 'renter' ? (
        <>
          <RenterCharacterPanel bot={bot} caps={caps} rental={rental} />
          <RenterSchedulePanel bot={bot} caps={caps} />
        </>
      ) : null}

      {layout === 'compact' ? (
        <>
          <div className="console-tabs" role="tablist" aria-label="Console sections">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                id={`tab-${t}`}
                aria-selected={tab === t}
                aria-controls={`panel-${t}`}
                className={`console-tab${tab === t ? ' console-tab-on' : ''}`}
                onClick={() => setTab(t)}
              >
                {t === 'compose' ? 'Compose' : t === 'queue' ? 'Queue' : t === 'feed' ? 'Feed' : 'Auto-reply'}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
            {tab === 'compose' ? compose : tab === 'queue' ? queue : tab === 'feed' ? feed : autoreply}
          </div>
        </>
      ) : (
        <>
          <div className="console-grid">
            <div className="console-col console-col-compose">{compose}</div>
            <div className="console-col console-col-queue">{queue}</div>
          </div>
          <div className="console-feed">{feed}</div>
          {autoreply ? <div className="console-feed">{autoreply}</div> : null}
        </>
      )}
    </div>
  );
}
